import React, { useRef, useEffect, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useFullScreenHandle } from 'react-full-screen'
import { PMTILES_URL } from '../const/general'
import { PMTILES_PREF_KEY } from '../config/config'
import { BASE_LAYER_LIST, BASE_LAYER_KEY } from '../const/map_config'
import {
    setSrRiskColor,
} from '../func/color';

import {
    StyleFullScreen,
    MapWrapper
} from '../style/map_components';

import MapFullscreenIcon from '../item/map_fullscreen_icon'
import MapSwitchLayerIcon from '../item/map_switch_layer_icon'
import MapSoilRainLegendIcon from '../item/map_soil_rain_legend_icon'
import '../style/legend_map.css';
import bbox from '@turf/bbox';
import choropleth_city from '../data/city.json';
import choropleth_area from '../data/area.json';
import choropleth_msh from '../data/msh.json';

const sourceSetting = {
    "osm": {
        "type": "raster",
        "tiles": ["https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg"],
        "tileSize": 256,
        "attribution": "MapLibre | 地図の出典：<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>",
    },
    "city": {
        "type": "vector",
        "tiles": [`${PMTILES_URL}/${PMTILES_PREF_KEY}/city/{z}/{x}/{y}.mvt`],
    },
    "area": {
        "type": "vector",
        "tiles": [`${PMTILES_URL}/${PMTILES_PREF_KEY}/area/{z}/{x}/{y}.mvt`],
    },
    "msh": {
        "type": "vector",
        "tiles": [`${PMTILES_URL}/${PMTILES_PREF_KEY}/msh/{z}/{x}/{y}.mvt`],
    },
};

// 背景地図の設定
const baseLayer = [
    {
        "id": "osm-tiles",
        "type": "raster",
        "source": "osm",
        "minzoom": 0,
        "maxzoom": 24,
    }
];

// PMTilesから取得して表示、非表示を制御するレイヤ達
const otherLayers = [
    {
        "id": "city",
        "source": "city",
        "source-layer": "city",
        "type": "fill",
        "paint": {
            "fill-color": "#5c9fed",
            "fill-opacity": 0.8,
            // "fill-outline-color": "#ffffff",
        },
        "layout": {
            "visibility": "none" // 最初から表示する場合は "visible"に
        }
    },
    {
        "id": "area",
        "source": "area",
        "source-layer": "area",
        "type": "fill",
        "paint": {
            "fill-color": "#5c9fed",
            "fill-opacity": 0.8,
            // "fill-outline-color": "#ffffff",
        },
        "layout": {
            "visibility": "none" // 最初から表示する場合は "visible"に
        }
    },
    {
        "id": "msh",
        "source": "msh",
        "source-layer": "msh",
        "type": "fill",
        "paint": {
            "fill-color": "#5c9fed",
            "fill-opacity": 0.8,
        },
        "layout": {
            "visibility": "none" // 最初から表示する場合は "visible"に
        }
    }
];

const MapComponent = (props) => {

    const mapContainer = useRef(null);
    const map = useRef(null);

    // 監視用Ref
    const selectCitysRef = useRef(props.selectCitys);
    const citySoilRainLvH1Ref = useRef(props.citySoilRainLvH1);
    const areaSoilRainLvH1Ref = useRef(props.areaSoilRainLvH1);
    const mshSoilRainLvH1Ref = useRef(props.mshSoilRainLvH1);

    const mapStyle = {
        height: '100%',
        width: '100%',
        margin: '0 auto',
    }

    // 全画面表示切り替え
    const fullscreenHandle = useFullScreenHandle()

    const layersSetting = [...baseLayer, ...otherLayers]

    let hoveredId = null

    const filterData = () => {
        switch (props.selectLayer.category) {
            case BASE_LAYER_KEY.CITY:
                return citySoilRainLvH1Ref.current
            case BASE_LAYER_KEY.AREA:
                return areaSoilRainLvH1Ref.current
            case BASE_LAYER_KEY.MSH:
                return mshSoilRainLvH1Ref.current
            default:
                return citySoilRainLvH1Ref.current
        }
    }

    // レイヤー切り替え
    const switchLayer = () => {
        if (map.current && map.current.isStyleLoaded()) {
            BASE_LAYER_LIST.map((obj) => {
                if (obj.category === props.selectLayer.category) {
                    map.current.setLayoutProperty(obj.category,
                        'visibility', 'visible');
                    map.current.on('mouseenter', props.selectLayer.category, (e) => { handleMouseMove(e) });
                    map.current.on('mousemove', props.selectLayer.category, (e) => { handleMouseMove(e) });
                    map.current.on('mouseleave', props.selectLayer.category, handleMouseLeave);
                } else {
                    map.current.setLayoutProperty(obj.category,
                        'visibility', 'none');
                    map.current.off('mouseenter', props.selectLayer.category, (e) => { handleMouseMove(e) });
                    map.current.off('mousemove', props.selectLayer.category, (e) => { handleMouseMove(e) });
                    map.current.off('mouseleave', props.selectLayer.category, handleMouseLeave);
                }
            });
        }
    }

    // レイヤーの色分け
    const setLayerStyle = () => {

        if (!map.current || !map.current.isStyleLoaded()) return;
        switchLayer()
        // 対象のデータを選ぶ
        let riskData = filterData();

        if (props.selectSubCity.length > 0) {
            setMapCenterZoom(BASE_LAYER_KEY.CITY, props.selectSubCity)
        }
        if (props.selectArea.length > 0) {
            setMapCenterZoom(BASE_LAYER_KEY.AREA, props.selectArea)
        }
        if (props.selectMsh.length > 0) {
            setMapCenterZoom(BASE_LAYER_KEY.MSH, props.selectMsh)
        }

        const matchExpression_fill = ['match', ['get', 'id']];
        if (!riskData || riskData === undefined || riskData === null || riskData.length < 1) {
            map.current.setPaintProperty(props.selectLayer.category, 'fill-color', 'rgba(0,0,0,0)');
            return
        }
        for (const item of riskData) {
            matchExpression_fill.push(
                item.code,
                setSrRiskColor(item.sr_lv).color);
        }
        matchExpression_fill.push('rgba(0, 0, 0, 0)');

        map.current.setPaintProperty(props.selectLayer.category, 'fill-color', matchExpression_fill);

        // リセットされた場合
        if (props.selectSubCity.length === 0 && props.selectArea.length === 0 && props.selectMsh.length === 0) {
            setMapCenterZoom(props.selectLayer.category, riskData.map(data => data.code))
        }
    }

    // 地図のセンターとズーム調整
    const setMapCenterZoom = (selectLayer_category, ids) => {
        if (!map.current || ids.length === 0) return;

        const features =
            (selectLayer_category === BASE_LAYER_KEY.CITY) ? choropleth_city.features
                : (selectLayer_category === BASE_LAYER_KEY.AREA) ? choropleth_area.features
                    : choropleth_msh.features;

        
        // 対象IDの feature を抽出
        const filteredFeatures = features.filter(f =>
            ids.includes(f.properties.id)
        );

        if (filteredFeatures.length === 0) {
            console.warn("対象フィーチャが見つかりません");
            return;
        }

        // 抽出したフィーチャのコレクションを作成
        const filteredCollection = {
            type: "FeatureCollection",
            features: filteredFeatures
        };

        // Turf.js で bbox を取得
        const bounds = bbox(filteredCollection); // [west, south, east, north]

        map.current.fitBounds(bounds, {
            padding: 30,
            maxZoom: 13,   // 拡大制限
            duration: 800  // アニメーション時間
        });
    }

    // レイヤーマウスホバー時の処理
    const handleMouseMove = (e) => {

        const feature = e.features[0];
        if (!feature) return;
        const id = feature.properties.id;
        const properties = feature.properties;
        // すでにホバー中なら無視
        if (hoveredId === id) return;

        const riskData = filterData()
        if (!riskData || riskData === undefined || riskData === null || riskData.length < 1) return

        const data = riskData.find(item => item.code === Number(id));

        // 前のポップアップを削除
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
        }
        if (!data) {
            map.current.getCanvas().style.cursor = '';
            return;
        } else {
            map.current.getCanvas().style.cursor = 'pointer';
        }
        hoveredId = id;

        const nameStr = (props.selectLayer.category === BASE_LAYER_KEY.CITY) ? "市町村" :
            (props.selectLayer.category === BASE_LAYER_KEY.AREA) ? "字" :
                "メッシュ"

        const popup = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false
        })
            .setLngLat(e.lngLat)
            .setHTML(`
                <div>${nameStr}：<strong>${properties.name}</strong></div>
                <div>土壌雨量指数レベル：<strong>${data.sr_lv}</strong></div>
                <div>土壌雨量指数：<strong>${data.sr_val}</strong></div>
            `)
            .addClassName('custom-popup')
            .addTo(map.current);

        map.current.__hoverPopup = popup;
    };

    // レイヤーマウスアウト時の処理
    const handleMouseLeave = () => {
        map.current.getCanvas().style.cursor = '';
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
            hoveredId = null
        }
    };

    // レイヤークリック時の処理
    const handleClick = (e, category) => {
        const feature = e.features[0];
        const featureId = feature.properties.id;
        switch (category) {
            case BASE_LAYER_KEY.CITY:
                props.setSelectSubCity([featureId])
                props.setSelectArea([])
                props.setSelectMsh([])
                break
            case BASE_LAYER_KEY.AREA:
                props.setSelectSubCity([])
                props.setSelectArea([featureId])
                props.setSelectMsh([])
                // props.setSelectBsn([])
                break
            case BASE_LAYER_KEY.MSH:
                props.setSelectSubCity([])
                props.setSelectArea([])
                props.setSelectMsh([featureId])
                // props.setSelectBsn([])
                break
            default:
                props.setSelectSubCity([])
                props.setSelectArea([])
                props.setSelectMsh([])
                // props.setSelectBsn([])
                break
        }
    };

    // ロード時の処理
    const handleLoad = () => {
        if (!map.current) return;
        setLayerStyle()
        map.current.on('click', BASE_LAYER_KEY.CITY, (e) => handleClick(e, BASE_LAYER_KEY.CITY));
        map.current.on('click', BASE_LAYER_KEY.AREA, (e) => handleClick(e, BASE_LAYER_KEY.AREA));
        map.current.on('click', BASE_LAYER_KEY.MSH, (e) => handleClick(e, BASE_LAYER_KEY.MSH));
        map.current.getCanvas().addEventListener('mouseleave', handleMouseLeave);
    };

    // マップ初期化処理
    useEffect(() => {
        if (map.current) return; // initialize map only once

        map.current = new maplibregl.Map({
            container: mapContainer.current,
            center: props.riskMapCenter,
            zoom: props.riskMapZoom,
            style: {
                version: 8,
                sources: sourceSetting,
                layers: layersSetting
            },
            attributionControl: false
        });
        map.current.addControl(new maplibregl.NavigationControl({
            // コンパス表示なし
            showCompass: false,
            showZoom: true,
        }), 'top-left');
        map.current.addControl(new maplibregl.AttributionControl({
            // Attributionは折りたたまない
            compact: false,
        }), 'bottom-right');

        map.current.on('load', handleLoad);

        return () => {
            if (!map.current) return;
            map.current.off('load', handleLoad);
            map.current.off('click', BASE_LAYER_KEY.CITY, (e) => handleClick(e, BASE_LAYER_KEY.CITY));
            map.current.off('click', BASE_LAYER_KEY.AREA, (e) => handleClick(e, BASE_LAYER_KEY.AREA));
            map.current.off('click', BASE_LAYER_KEY.MSH, (e) => handleClick(e, BASE_LAYER_KEY.MSH));
            map.current.off('mouseenter', BASE_LAYER_KEY.CITY, (e) => { handleMouseMove(e) });
            map.current.off('mouseenter', BASE_LAYER_KEY.AREA, (e) => { handleMouseMove(e) });
            map.current.off('mouseenter', BASE_LAYER_KEY.MSH, (e) => { handleMouseMove(e) });
            map.current.off('mousemove', BASE_LAYER_KEY.CITY, (e) => { handleMouseMove(e) });
            map.current.off('mousemove', BASE_LAYER_KEY.AREA, (e) => { handleMouseMove(e) });
            map.current.off('mousemove', BASE_LAYER_KEY.MSH, (e) => { handleMouseMove(e) });
            map.current.off('mouseleave', BASE_LAYER_KEY.CITY, handleMouseLeave);
            map.current.off('mouseleave', BASE_LAYER_KEY.AREA, handleMouseLeave);
            map.current.off('mouseleave', BASE_LAYER_KEY.MSH, handleMouseLeave);
            map.current.off('styledata', setLayerStyle);
            map.current.getCanvas().removeEventListener('mouseleave', handleMouseLeave);
            map.current.remove();
            map.current = null;
        };
    }, []);

    // 市町村チェックボックス変更時に呼ばれる
    useEffect(() => {

        selectCitysRef.current = props.selectCitys
        if (props.selectSubCity.length > 0) props.setSelectSubCity([]);
        if (props.selectArea.length > 0) props.setSelectArea([]);
        if (props.selectMsh.length > 0) props.setSelectMsh([]);

        // 対象のデータを選ぶ
        let riskData = filterData();

        if (riskData && riskData !== undefined && riskData !== null && riskData.length > 0) {
            if (map.current?.isStyleLoaded()) {
                setLayerStyle();
            }
        }
    }, [props.selectCitys]);

    // レイヤー切り替え時
    useEffect(() => {
        if (props.shouldClearSelect) {
            props.setSelectSubCity([])
            props.setSelectArea([]);
            props.setSelectMsh([]);
        } else {
            setLayerStyle();
        }
        props.setShouldClearSelect(false);
    }, [props.selectLayer]);

    // データ更新時
    // 市町村チェックボックス変更時、
    // レイヤー切り替え時
    // 市町村レイヤークリック時
    // 字レイヤークリック時
    // メッシュレイヤークリック時
    // リセットボタンで個別の選択が解除されたときも呼ばれる
    useEffect(() => {
        citySoilRainLvH1Ref.current = props.citySoilRainLvH1
        areaSoilRainLvH1Ref.current = props.areaSoilRainLvH1
        mshSoilRainLvH1Ref.current = props.mshSoilRainLvH1

        if (!map.current) return;
        const interval = setInterval(() => {
            if (map.current?.isStyleLoaded()) {
                if (map.current.getLayer(props.selectLayer.category)) {
                    setLayerStyle();
                    clearInterval(interval);
                }
            }
        }, 100);

        return () => clearInterval(interval);
    }, [
        props.citySoilRainLvH1, props.areaSoilRainLvH1, props.mshSoilRainLvH1,
        props.selectSubCity, props.selectArea, props.selectMsh
    ]);

    return (
        <StyleFullScreen
            handle={fullscreenHandle}
            enabled={true}
        >
            <MapWrapper>
                <div
                    ref={mapContainer}
                    className=""
                    style={mapStyle} />
                <MapFullscreenIcon
                    fullscreenHandle={fullscreenHandle}
                    top = {'70px'}
                />
                <MapSwitchLayerIcon
                    selectLayer={props.selectLayer}
                    setSelectLayer={props.setSelectLayer}
                    selectArea={props.selectArea}
                    selectSubCity={props.selectSubCity}
                    selectMsh={props.selectMsh}
                    setSelectArea={props.setSelectArea}
                    setSelectSubCity={props.setSelectSubCity}
                    setSelectMsh={props.setSelectMsh}
                    setShouldClearSelect={props.setShouldClearSelect}
                />
                <MapSoilRainLegendIcon />
            </MapWrapper>
        </StyleFullScreen>
    );
};

export default MapComponent;